/**
 * WhatsApp messaging utilities
 */

interface WhatsAppMessageOptions {
  phoneNumber: string;
  message: string;
}

/**
 * Build a WhatsApp send URL (no window open).
 */
export function buildWhatsAppUrl(
  phoneNumber: string,
  message?: string
): string {
  const cleanPhone = phoneNumber.replace(/\D/g, "");
  const whatsappPhone = cleanPhone.startsWith("55")
    ? cleanPhone
    : `55${cleanPhone}`;
  const base = `https://api.whatsapp.com/send?phone=${whatsappPhone}`;
  return message
    ? `${base}&text=${encodeURIComponent(message)}`
    : base;
}

/**
 * Send a WhatsApp message by opening WhatsApp Web/App
 */
export const sendWhatsAppMessage = ({
  phoneNumber,
  message,
}: WhatsAppMessageOptions): void => {
  const whatsappUrl = buildWhatsAppUrl(phoneNumber, message);
  window.open(whatsappUrl, "_blank");
};

/**
 * Get default completion message for appointments
 */
export const getCompletionMessage = (name: string): string => {
  return `Olá ${name.split(" ")[0]}! Seu carro já está finalizado 🚗✨  
Cuidamos de cada detalhe com muita atenção,o resultado ficou incrível 🤩  

Me avise sua disponibilidade para retirada ou, se preferir, podemos organizar a entrega. ✅🫡

Foi um prazer receber seu carro aqui 😉`;
};

/** Pre-filled WhatsApp text for a client overdue for a service follow-up. */
export const getFollowUpWhatsAppMessage = (
  clientName: string,
  serviceName: string,
  daysSinceLastAppointment: number,
  expirationDays: number,
): string => {
  const first = clientName.trim().split(/\s+/)[0] || "Cliente";
  return `Olá, ${first}! Tudo bem?

Notamos que já faz ${daysSinceLastAppointment} dias desde o seu último ${serviceName}. O prazo recomendado para esse serviço é de ${expirationDays} dias.

Podemos agendar um novo horário para você? É só responder esta mensagem!

Estamos à disposição.`;
};

/** Pre-filled WhatsApp text to remind a client about an upcoming appointment. */
export const getAppointmentReminderWhatsAppMessage = (params: {
  clientName: string;
  companyName: string;
  appointmentDate: string;
  startTime?: string;
  serviceName: string;
  carModel?: string;
  carPlate?: string;
}): string => {
  const first = params.clientName.trim().split(/\s+/)[0] || "Cliente";
  const dateLabel = new Date(`${params.appointmentDate}T12:00:00`).toLocaleDateString(
    "pt-BR",
    {
      timeZone: "America/Sao_Paulo",
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }
  );
  const timeLine = params.startTime
    ? `\n🕐 Horário: ${params.startTime.slice(0, 5)}`
    : "";
  const carLine =
    params.carModel && params.carPlate
      ? `\n🚗 Veículo: ${params.carModel} · ${params.carPlate}`
      : "";

  return `Olá, ${first}! Tudo bem?

Passando para confirmar seu agendamento na ${params.companyName}:
📅 ${dateLabel}${timeLine}${carLine}
✨ Serviço: ${params.serviceName}

Aguardamos você! Qualquer dúvida, estaremos à disposição.`;
};

/** Pre-filled WhatsApp text for sharing a budget with a client. */
export const getBudgetWhatsAppMessage = (params: {
  clientName: string;
  companyName: string;
  budgetId: number;
  services: {
    name: string;
    price: number;
    subServices?: string[];
  }[];
  total: number;
  createdAt: string;
  carLabel?: string;
  isPaid?: boolean;
}): string => {
  const first = params.clientName.trim().split(/\s+/)[0] || "Cliente";
  const dateLabel = new Date(`${params.createdAt.split("T")[0]}T12:00:00`).toLocaleDateString(
    "pt-BR",
    { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric" }
  );

  const lines = params.services
    .map((s) => {
      const subs =
        s.subServices && s.subServices.length > 0
          ? `\n${s.subServices.map((sub) => `  - ${sub}`).join("\n")}`
          : "";
      return `• ${s.name}: ${s.price.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })}${subs}`;
    })
    .join("\n");

  const total = params.total.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const carLine = params.carLabel ? `\n🚗 Veículo: ${params.carLabel}` : "";

  if (params.isPaid) {
    return `Olá, ${first}! Tudo bem?

Segue o comprovante de pagamento #${params.budgetId} da ${params.companyName} (${dateLabel}):${carLine}

${lines}

*Total pago: ${total}*

Obrigado pela preferência! Qualquer dúvida, estaremos à disposição.`;
  }

  return `Olá, ${first}! Tudo bem?

Segue o orçamento #${params.budgetId} da ${params.companyName} (${dateLabel}):${carLine}

${lines}

*Total: ${total}*

Orçamento válido por 7 dias. Qualquer dúvida ou para confirmar, estaremos à disposição.`;
};

/** Pre-filled WhatsApp text for a client whose birthday is today. */
export const getBirthdayWhatsAppMessage = (_fullName: string): string => {
  return `Hoje é um dia muito especial… o seu dia! 🎉  

Quero te desejar um novo ciclo cheio da presença de Deus, que Ele continue te guiando, te protegendo e abençoando cada passo da sua vida ✨  
Que não te falte paz, saúde e motivos pra sorrir.  🙏🏻

É uma alegria ter você com a gente aqui na RD08, fazer parte da sua história e cuidar de algo que também é importante pra você 💛  

Que Deus abençoe ainda mais o seu novo ano de vida! Conte sempre com a gente!

Rafael
GARAGEM RD08
😉🤝`;
};

/**
 * WhatsApp button component props interface
 */
export interface WhatsAppButtonProps {
  phoneNumber: string;
  message?: string;
  className?: string;
  title?: string;
  size?: "sm" | "md" | "lg";
}

/**
 * Get WhatsApp button styles based on size
 */
export const getWhatsAppButtonStyles = (
  size: "sm" | "md" | "lg" = "md"
): string => {
  const baseStyles =
    "inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-full shadow-md hover:shadow-lg transform transition-all duration-200 hover:scale-105 active:scale-95";

  const sizeStyles = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-10 h-10",
  };

  return `${baseStyles} ${sizeStyles[size]}`;
};

/**
 * Get WhatsApp icon SVG based on size
 */
export const getWhatsAppIconStyles = (
  size: "sm" | "md" | "lg" = "md"
): string => {
  const iconStyles = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  return iconStyles[size];
};

/**
 * WhatsApp icon SVG path
 */
export const WHATSAPP_ICON_PATH =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.515";
